import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { objectToArray } from 'utils/helpers';
import * as icons from '@shopify/polaris-icons';
import React from 'react';
import type { ArticleIndex, NavigationSection, NavigationSubItem } from '@/shared/typings/types/guide';

export type NavigationItem = Omit<NavigationSection, 'icon'> & {
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>> | null;
  subNavigationItems?: NavigationSubItem[];
  onClick?: () => void;
};

type NavigationState = {
  sections: Record<string, NavigationSection>;
  sections_list: NavigationSection[];
  navigation_items: NavigationItem[];
  navigation_items_unassigned: NavigationItem[] | null;
  pinned_articles: NavigationSubItem[];
  first_article_id: string | null;
  articles_index: Record<string, ArticleIndex>;
  _init: boolean;
};

const initialState: NavigationState = {
  sections: {},
  sections_list: [],
  navigation_items: [],
  navigation_items_unassigned: null,
  pinned_articles: [],
  first_article_id: null,
  articles_index: {},
  _init: false,
};

const articleIndexToNav = (a: ArticleIndex): NavigationSubItem => ({
  ...a,
  label: a.title,
  url: `/articles/${a.id}`,
});

function resolveIcon(name: string | undefined | null): React.FunctionComponent<React.SVGProps<SVGSVGElement>> | null {
  if (!name) return null;
  if ((icons as Record<string, unknown>)[name]) {
    return (icons as Record<string, React.FunctionComponent<React.SVGProps<SVGSVGElement>>>)[name];
  }
  
  let cleanName = name.replace(/(Major|Minor)$/, '');
  if (cleanName === 'CirclePlus') cleanName = 'PlusCircle';
  if (cleanName === 'MobileHamburger') cleanName = 'Menu';
  if (cleanName === 'LogOut') cleanName = 'Exit';
  if (cleanName === 'Delete') cleanName = 'Delete';
  
  const iconName = `${cleanName}Icon`;
  return (icons as Record<string, React.FunctionComponent<React.SVGProps<SVGSVGElement>>>)[iconName] ||
         (icons as Record<string, React.FunctionComponent<React.SVGProps<SVGSVGElement>>>)[cleanName] ||
         null;
}

function buildNavigation(
  sections: Record<string, NavigationSection>,
  articles_index: Record<string, ArticleIndex>
) {
  let navigation_items_unassigned: NavigationItem[] | null = null;
  const articles = Object.values(articles_index).map(articleIndexToNav);
  const unassignedArticles = articles.map(a => a.id);
  const sortIndex = (a: { index: number }, b: { index: number }) => a.index > b.index ? 1 : -1;
  const sortTitle = (a: { title: string }, b: { title: string }) => a.title > b.title ? 1 : -1;
  const sortArticles = (a: NavigationSubItem, b: NavigationSubItem) => {
    const aIndex = a.index !== undefined ? a.index : Number.MAX_SAFE_INTEGER;
    const bIndex = b.index !== undefined ? b.index : Number.MAX_SAFE_INTEGER;
    if (aIndex !== bIndex) {
      return aIndex - bIndex;
    }
    return a.title.localeCompare(b.title);
  };

  const navigation_items: NavigationItem[] = (objectToArray(sections) as NavigationSection[])
    .map(s => {
      const subItems = articles
        .filter(a => a.section === s.id)
        .sort(sortArticles)
        .map(a => {
          const index = unassignedArticles.indexOf(a.id);
          if (index > -1) {
            unassignedArticles.splice(index, 1);
          }
          return a;
        });

      return {
        ...s,
        icon: resolveIcon(s.icon),
        subNavigationItems: subItems,
      };
    })
    .sort(sortIndex);

  if (unassignedArticles.length > 0) {
    navigation_items_unassigned = [{
      id: '__unassigned',
      label: 'Unassigned',
      icon: null,
      index: -1,
      subNavigationItems: unassignedArticles.map(id => articleIndexToNav(articles_index[id])),
    }];
  }

  const pinned_articles = articles.filter(a => a.pinned);

  return { navigation_items, navigation_items_unassigned, pinned_articles };
}

export const navigationSlice = createSlice({
  name: 'navigation',
  initialState,
  reducers: {
    setNavigation(state, { payload }: PayloadAction<{ sections?: Record<string, NavigationSection>; articles_index?: Record<string, ArticleIndex> } | undefined>) {
      const { sections = {}, articles_index = {} } = payload || {};
      const nav = buildNavigation(sections, articles_index);
      const { navigation_items } = nav;
      const sectionsWithItems = navigation_items.filter(i => i.subNavigationItems.length > 0);
      const first_article_id = sectionsWithItems.length > 0 ? sectionsWithItems[0].subNavigationItems[0].id : null;

      state.sections = sections;
      state.sections_list = objectToArray(sections) as NavigationSection[];
      state.navigation_items = nav.navigation_items;
      state.navigation_items_unassigned = nav.navigation_items_unassigned;
      state.pinned_articles = nav.pinned_articles;
      state.first_article_id = first_article_id;
      state.articles_index = articles_index;
      state._init = true;
    },
    setArticleIndex(state, { payload }: PayloadAction<ArticleIndex>) {
      const articles_index = { ...state.articles_index, [payload.id]: payload };
      const nav = buildNavigation(state.sections, articles_index);
      state.navigation_items = nav.navigation_items;
      state.navigation_items_unassigned = nav.navigation_items_unassigned;
      state.pinned_articles = nav.pinned_articles;
      state.articles_index = articles_index;
    },
    deleteArticleIndex(state, { payload }: PayloadAction<{ id: string }>) {
      const articles_index = { ...state.articles_index };
      delete articles_index[payload.id];
      const nav = buildNavigation(state.sections, articles_index);
      state.navigation_items = nav.navigation_items;
      state.navigation_items_unassigned = nav.navigation_items_unassigned;
      state.pinned_articles = nav.pinned_articles;
      state.articles_index = articles_index;
    },
  },
});

export const {
  setNavigation,
  setArticleIndex,
  deleteArticleIndex,
} = navigationSlice.actions;

export const getNavigation = (state: RootState): NavigationState => state.navigation;
export const getNavigationInit = (state: RootState): boolean => state.navigation._init;
export const getSections = (state: RootState): Record<string, NavigationSection> => state.navigation.sections;
export const getSectionsList = (state: RootState): NavigationSection[] => state.navigation.sections_list;
export const getNavigationItems = (state: RootState): NavigationItem[] => state.navigation.navigation_items;
export const getNavigationItemsUnassigned = (state: RootState): NavigationItem[] | null => state.navigation.navigation_items_unassigned;
export const getPinnedArticles = (state: RootState): NavigationSubItem[] => state.navigation.pinned_articles;
export const getFirstArticleId = (state: RootState): string | null => state.navigation.first_article_id;
export const getArticlesIndex = (state: RootState): Record<string, ArticleIndex> => state.navigation.articles_index;

export default navigationSlice.reducer;
